import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

import {
  argumentoAResaltar,
  argumentosDeFirma,
  buscarFunciones,
  CATEGORIAS,
  FUNCIONES,
  funcionPorNombre,
  normalizar,
} from "../shared/ayudaFunciones";
import {
  enPosicionDeArgumento,
  esFormula,
  funcionEnCursor,
  hojaEnFormula,
  insertar,
  insertarFuncion,
  palabraEnCursor,
  textoDeRef,
} from "../shared/cursorFormula";
import { refDe } from "../shared/planilla";

import "./css/asistenteFormula.css";

/* El asistente de fórmulas de FiboSheets: armar una fórmula sin saberla.
 *
 * Igna, 05/10/2026: las funciones estaban todas, pero escondidas en un párrafo
 * al pie. Esto le suma a la planilla lo que Excel y Google Sheets hacen
 * mientras se escribe una fórmula, en la celda o en la barra:
 *
 *   · AUTOCOMPLETAR: escribir letras donde va una función (después de «=»,
 *     «(», «;», un operador) despliega las que empiezan —o contienen— eso, con
 *     su firma y qué hacen. Flechas y Enter/Tab, o tocar, la eligen: queda
 *     `NOMBRE(` con el cursor adentro. Las HUB.… salen con su rayito, y el
 *     nombre en inglés (VLOOKUP) lleva al de acá (BUSCARV).
 *   · AYUDA DEL ARGUMENTO: con el cursor adentro de FUNCION(…) se ve la firma
 *     con el argumento que toca resaltado, y qué hace.
 *   · TOCAR UNA CELDA PONE SU REFERENCIA: si el cursor está donde va un
 *     argumento, un clic en otra celda escribe `B4` (y otro clic la cambia),
 *     arrastrar escribe `B4:B10`, y Shift+clic estira hasta ahí. Si se pasa a
 *     otra hoja en el medio, la fórmula sigue en una tira arriba de la grilla
 *     y las referencias salen con la hoja: `'Hoja 2'!B4`. Si el cursor NO está
 *     donde va un argumento, el clic confirma la edición, como siempre.
 *   · EL BOTÓN «fx»: un buscador por categoría con descripción y ejemplo.
 *
 * ── POR QUÉ ESCUCHA EL DOCUMENTO Y NO LAS CELDAS ──────────────────────────
 *
 * MiEspacio.jsx es grande y lo tocan varios a la vez. Para no meterse en su
 * manejo del teclado y del mouse, el asistente escucha en el `document`, en
 * fase de CAPTURA: llega antes que los manejadores de React (que escuchan en
 * la raíz) y, cuando algo es suyo (Enter con la lista abierta, un clic en una
 * celda que es una referencia), lo frena ahí y la planilla ni se entera.
 * Cuando no es suyo, no toca nada. A la planilla sólo le pide cuatro cosas:
 * escribir la celda que se edita, escribir una celda de otra hoja, volver a
 * una celda, y la barra de fórmulas.
 *
 * Qué input se está asistiendo se lee del DOM: la celda con la clase
 * `editando`, la barra, o la tira de otra hoja; y sólo si lo escrito empieza
 * con «=». La lógica de «dónde está el cursor» es pura y vive en
 * `shared/cursorFormula`; la ayuda de cada función, en `shared/ayudaFunciones`.
 *
 * ── EN EL CELULAR ──────────────────────────────────────────────────────────
 *
 * La lista y la ayuda se ubican al lado de lo que se edita (abajo, o arriba
 * si abajo no entra: el teclado del teléfono se come media pantalla, por eso
 * se mide con `visualViewport`), nunca encima. Cada opción tiene alto de dedo,
 * y tocarla no le saca el foco a la celda, así el teclado no se cierra. */

/* Arriba o abajo del input, lo que entre. */
function ubicar(el, quiereAlto) {
  const r = el.getBoundingClientRect();
  const vv = typeof window !== "undefined" ? window.visualViewport : null;
  const vpTop = vv ? vv.offsetTop : 0;
  const vpAlto = vv ? vv.height : window.innerHeight;
  const abajo = vpTop + vpAlto - r.bottom - 8;
  const arriba = r.top - vpTop - 8;
  const ancho = Math.min(400, window.innerWidth - 16);
  const left = Math.max(8, Math.min(r.left, window.innerWidth - ancho - 8));
  const vaAbajo = abajo >= Math.min(quiereAlto, 180) || abajo >= arriba;
  return { r, ancho, left, vaAbajo, abajo, arriba, altoVentana: window.innerHeight };
}

/* La firma con el argumento actual resaltado. */
function Firma({ fn, indice = -1 }) {
  const args = argumentosDeFirma(fn.firma);
  const hi = indice < 0 ? -1 : argumentoAResaltar(args, indice);
  return (
    <code className="asf-firma">
      {fn.nombre}(
      {args.map((a, i) => (
        <span key={i}>
          <span className={i === hi ? "asf-arg-actual" : undefined}>{a}</span>
          {i < args.length - 1 ? "; " : ""}
        </span>
      ))}
      )
    </code>
  );
}

const Icono = ({ fn }) => (
  <span className={"asf-icono" + (fn.hub ? " hub" : "")} aria-hidden="true" title={fn.hub ? "Dato del hub" : undefined}>
    {fn.hub ? "⚡" : "ƒ"}
  </span>
);

/* ── El buscador del botón «fx» ─────────────────────────────────────────── */

function BuscadorFunciones({ puedeInsertar, onElegir, onCerrar }) {
  const [busca, setBusca] = useState("");
  const [categoria, setCategoria] = useState(CATEGORIAS[0]);
  const lista = useMemo(() => {
    const q = normalizar(busca.trim());
    if (!q) return FUNCIONES.filter((f) => f.categoria === categoria);
    // Por el nombre primero (como el autocompletar) y después por lo que hace:
    // «redondear», «días hábiles», «buscar».
    const porNombre = buscarFunciones(busca.trim(), 200).map((r) => r.fn);
    const porQueHace = FUNCIONES.filter((f) => !porNombre.includes(f) && normalizar(f.descripcion).includes(q));
    return [...porNombre, ...porQueHace];
  }, [busca, categoria]);

  return (
    <div className="asf-fondo" onMouseDown={onCerrar}>
      <section
        className="asf-fx" role="dialog" aria-label="Buscar una función"
        onMouseDown={(e) => e.stopPropagation()}
        onKeyDown={(e) => { if (e.key === "Escape") { e.preventDefault(); onCerrar(); } }}
      >
        <div className="asf-fx-cabeza">
          <b>Insertar una función</b>
          <button type="button" className="esp-btn chico" onClick={onCerrar} aria-label="Cerrar el buscador">×</button>
        </div>
        <input
          className="asf-fx-busca" autoFocus value={busca} spellCheck={false} autoComplete="off"
          placeholder="Buscá por nombre o por lo que hace…" aria-label="Buscar función"
          onChange={(e) => setBusca(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && lista[0] && puedeInsertar) { e.preventDefault(); onElegir(lista[0]); } }}
        />
        {!busca.trim() && (
          <div className="asf-fx-cats" role="group" aria-label="Categorías">
            {CATEGORIAS.map((c) => (
              <button
                key={c} type="button" aria-pressed={c === categoria}
                className={"asf-fx-cat" + (c === categoria ? " activa" : "")} onClick={() => setCategoria(c)}
              >{c}</button>
            ))}
          </div>
        )}
        {!puedeInsertar && <p className="asf-fx-nota">Elegí una celda para insertar la función ahí.</p>}
        <ul className="asf-fx-lista">
          {lista.map((fn) => (
            <li key={fn.nombre}>
              <button
                type="button" className="asf-fx-item" disabled={!puedeInsertar}
                onClick={() => onElegir(fn)} aria-label={`Insertar ${fn.nombre}`}
              >
                <span className="asf-fx-linea"><Icono fn={fn} /><Firma fn={fn} /></span>
                <span className="asf-desc">{fn.descripcion}</span>
                <span className="asf-ej">Ej.: <code>{fn.ejemplo}</code></span>
              </button>
            </li>
          ))}
          {lista.length === 0 && <li className="asf-fx-nota">No hay ninguna con «{busca}».</li>}
        </ul>
      </section>
    </div>
  );
}

/* ── El asistente ───────────────────────────────────────────────────────── */

/* Props (lo que la planilla le presta):
 *   activo       false en una planilla ajena (sólo lectura): no hace nada.
 *   barraRef     el input de la barra de fórmulas.
 *   hojas        los nombres de las hojas, en orden; `activa` la que se ve.
 *   sel          la celda elegida { r, c } (la que se edita), o null.
 *   escribir(v)  escribe la celda elegida (como tipear en ella).
 *   escribirEn(i, r, c, v)  escribe una celda de la hoja i.
 *   volverA(i, r, c)        muestra la hoja i con esa celda elegida. */
export default function AsistenteFormula({ activo = true, barraRef, hojas = [], activa = 0, sel = null, escribir, escribirEn, volverA }) {
  // Lo que se está escribiendo: { tipo: "celda" | "barra" | "tira", valor, caret, fin }.
  const [foco, setFoco] = useState(null);
  const [eleccion, setEleccion] = useState({ clave: null, i: 0 }); // la opción marcada con las flechas
  const [cerrada, setCerrada] = useState(null); // dónde se cerró la lista con Escape
  const [tira, setTira] = useState(null); // la fórmula que sigue en otra hoja: { origen, r, c, valor }
  const [fx, setFx] = useState(false);
  const [, setTic] = useState(0); // para reubicar la lista al desplazar

  const elRef = useRef(null); // el input que se asiste
  const tiraRef = useRef(null);
  const listaRef = useRef(null);
  const caretPendiente = useRef(null); // { el | "tira", pos }: dónde poner el cursor después de escribir
  const ultimaRef = useRef(null); // la referencia recién puesta con un clic (otro clic la cambia)
  const arrastre = useRef(null);
  const ultimoFoco = useRef(null); // el último input con fórmula, para el «fx»
  const tiraCaret = useRef(0);
  const tocado = useRef(0); // cuándo se puso una referencia con el mouse (para frenar el doble clic)

  /* ¿Este input es uno que se asiste? */
  const tipoDe = (el) => {
    if (!el || el.tagName !== "INPUT") return null;
    if (el === tiraRef.current) return "tira";
    if (barraRef && el === barraRef.current) return "barra";
    const td = el.closest && el.closest("td[data-r]");
    return td && td.classList.contains("editando") ? "celda" : null;
  };

  /* Lee del DOM qué se está escribiendo y dónde está el cursor. */
  const sincronizar = (el = document.activeElement) => {
    const tipo = activo ? tipoDe(el) : null;
    if (!tipo || !esFormula(el.value)) {
      elRef.current = null;
      // El «fx» inserta en la última fórmula que se estaba escribiendo, salvo
      // que el foco se haya ido a otra cosa (otra celda, otro campo). Irse al
      // buscador del «fx» mismo, o a ningún lado, no cuenta.
      if (el && el !== document.body && el.closest && !el.closest(".asf-fx, .asf-boton-fx")) ultimoFoco.current = null;
      setFoco((f) => (f === null ? f : null));
      return;
    }
    elRef.current = el;
    const nuevo = { tipo, valor: el.value, caret: el.selectionStart ?? el.value.length, fin: el.selectionEnd ?? el.value.length };
    ultimoFoco.current = { el, ...nuevo };
    if (tipo === "tira") tiraCaret.current = nuevo.caret;
    setFoco((f) => (f && f.tipo === nuevo.tipo && f.valor === nuevo.valor && f.caret === nuevo.caret && f.fin === nuevo.fin ? f : nuevo));
  };

  /* Pone `texto` en el input que se asiste y el cursor en `caret`. La celda y
     la barra se escriben por la planilla (es su estado, y así se deshace como
     cualquier tecla); la tira es de acá. */
  const aplicarTexto = (el, tipo, texto, caret) => {
    if (tipo === "tira") {
      setTira((t) => (t ? { ...t, valor: texto } : t));
      caretPendiente.current = { el: "tira", pos: caret };
    } else {
      caretPendiente.current = { el, pos: caret };
      escribir(texto);
    }
  };

  /* ── Lo que se calcula en cada dibujo ───────────────────────────────── */

  let palabra = null;
  let opciones = [];
  if (foco && foco.caret === foco.fin) {
    palabra = palabraEnCursor(foco.valor, foco.caret);
    // Con la palabra vacía, la lista sólo sale recién escrito el «=»; después
    // de un «(» o un «;» lo más probable es una celda, no una función.
    if (palabra && palabra.texto === "" && foco.valor.trim() !== "=") palabra = null;
    if (palabra) opciones = buscarFunciones(palabra.texto, 30);
  }
  const claveLista = palabra ? `${foco.tipo}|${foco.valor}|${palabra.desde}|${palabra.texto}` : null;
  const verLista = opciones.length > 0 && cerrada !== claveLista;
  // Recién escrito el «=» no hay nada marcado: Enter confirma la celda como
  // siempre. Con letras escritas, la primera ya viene marcada.
  const marcada = !verLista ? -1
    : eleccion.clave === claveLista ? eleccion.i
      : palabra.texto === "" ? -1 : 0;

  const enFn = foco ? funcionEnCursor(foco.valor, foco.caret) : null;
  const fnAyuda = enFn ? funcionPorNombre(enFn.nombre) : null;

  const elegirFuncion = (op) => {
    const el = elRef.current;
    if (!el) return;
    const tipo = tipoDe(el);
    const p = palabraEnCursor(el.value, el.selectionStart ?? el.value.length);
    if (!p) return;
    const r = insertarFuncion(el.value, p, op.fn.nombre, argumentosDeFirma(op.fn.firma).length === 0);
    aplicarTexto(el, tipo, r.texto, r.caret);
  };

  /* Dónde va la referencia de una celda tocada: reemplazando la que se acaba
     de poner con otro clic (si el cursor sigue al final de ella y no se tocó
     nada), o en el cursor si ahí va un argumento. null: no va. */
  const lugarParaRef = (el) => {
    const valor = el.value;
    const ini = el.selectionStart ?? valor.length;
    const fin = el.selectionEnd ?? valor.length;
    const u = ultimaRef.current;
    if (u && u.el === el && u.valor === valor && ini === u.hasta && fin === u.hasta) return { desde: u.desde, hasta: u.hasta, ancla: u.ancla };
    if (enPosicionDeArgumento(valor, ini, fin)) return { desde: ini, hasta: fin, ancla: null };
    return null;
  };
  // La hoja que va adelante de la referencia: sólo si se está mirando otra.
  const hojaDeRef = () => (tira && activa !== tira.origen ? hojas[activa] : null);

  const ponerRef = (el, lugar, rango) => {
    const tipo = tipoDe(el);
    const r = insertar(el.value, lugar.desde, lugar.hasta, textoDeRef(rango, hojaDeRef()));
    ultimaRef.current = { el, desde: lugar.desde, hasta: r.caret, valor: r.texto, ancla: { r: rango.r0, c: rango.c0 } };
    aplicarTexto(el, tipo, r.texto, r.caret);
  };

  const confirmarTira = () => {
    const t = tira;
    if (!t) return;
    setTira(null);
    escribirEn(t.origen, t.r, t.c, t.valor);
    volverA(t.origen, t.r, t.c);
  };
  const cancelarTira = () => {
    const t = tira;
    if (!t) return;
    setTira(null);
    volverA(t.origen, t.r, t.c);
  };

  /* ── Los manejadores del documento ──────────────────────────────────── */

  const api = useRef({});
  api.current = {
    keydown: (e) => {
      if (!verLista || e.target !== elRef.current) return;
      const n = opciones.length;
      const frenar = () => { e.preventDefault(); e.stopPropagation(); };
      if (e.key === "ArrowDown") { frenar(); setEleccion({ clave: claveLista, i: (marcada + 1) % n }); }
      else if (e.key === "ArrowUp") { frenar(); setEleccion({ clave: claveLista, i: marcada <= 0 ? n - 1 : marcada - 1 }); }
      else if ((e.key === "Enter" || (e.key === "Tab" && !e.shiftKey)) && marcada >= 0) { frenar(); elegirFuncion(opciones[marcada]); }
      else if (e.key === "Escape") { frenar(); setCerrada(claveLista); }
    },
    mousedown: (e) => {
      if (e.button) return;
      const el = elRef.current;
      if (!el || !e.target.closest) return;
      const td = e.target.closest("td[data-r]");
      if (td) {
        // En la celda que se está editando, el clic mueve el cursor: lo normal.
        if (td.contains(el)) return;
        const r = Number(td.dataset.r);
        const c = Number(td.dataset.c);
        const lugar = lugarParaRef(el);
        if (!lugar) {
          // No va un argumento: el clic confirma. En la tira (otra hoja), eso
          // es volver con la fórmula a su celda.
          if (tipoDe(el) === "tira") { e.preventDefault(); e.stopPropagation(); confirmarTira(); }
          return;
        }
        e.preventDefault(); // el foco se queda en lo que se edita
        e.stopPropagation(); // y la planilla no elige la celda tocada
        const ancla = e.shiftKey && lugar.ancla ? lugar.ancla : { r, c };
        ponerRef(el, lugar, { r0: ancla.r, c0: ancla.c, r1: r, c1: c });
        arrastre.current = { el, ancla, r, c };
        tocado.current = Date.now();
        return;
      }
      // Pasar a otra hoja con el cursor donde va un argumento: la fórmula sigue
      // en la tira, para tocar las celdas de la otra hoja.
      const pestana = e.target.closest(".esp-hojas [role=tab]");
      if (pestana && !tira && sel && tipoDe(el) !== "tira" && lugarParaRef(el)) {
        const i = Array.prototype.indexOf.call(pestana.parentElement.children, pestana);
        if (i < 0 || i === activa) return;
        tiraCaret.current = el.selectionStart ?? el.value.length;
        setTira({ origen: activa, r: sel.r, c: sel.c, valor: el.value });
        caretPendiente.current = { el: "tira", pos: tiraCaret.current };
        // El clic sigue su camino: la planilla cambia de hoja.
      }
    },
    mouseover: (e) => {
      const a = arrastre.current;
      if (!a || !e.target.closest) return;
      const td = e.target.closest("td[data-r]");
      if (!td) return;
      const r = Number(td.dataset.r);
      const c = Number(td.dataset.c);
      if (r === a.r && c === a.c) return;
      const u = ultimaRef.current;
      if (!u || u.el !== a.el || a.el.value !== u.valor) { arrastre.current = null; return; }
      a.r = r; a.c = c;
      ponerRef(a.el, u, { r0: a.ancla.r, c0: a.ancla.c, r1: r, c1: c });
    },
    mouseup: () => { arrastre.current = null; },
    dblclick: (e) => {
      // El doble clic sobre una celda que se acaba de poner como referencia no
      // tiene que ponerse a editarla.
      if (elRef.current && Date.now() - tocado.current < 600 && e.target.closest && e.target.closest("td[data-r]")) {
        e.preventDefault(); e.stopPropagation();
      }
    },
    sincronizar: (e) => sincronizar(e && e.type === "focusout" ? e.relatedTarget : document.activeElement),
    reubicar: () => { if (elRef.current) setTic((t) => t + 1); },
  };

  useEffect(() => {
    const llamar = (k) => (e) => api.current[k](e);
    const pares = [
      ["keydown", llamar("keydown"), true],
      ["mousedown", llamar("mousedown"), true],
      ["mouseover", llamar("mouseover"), true],
      ["mouseup", llamar("mouseup"), true],
      ["dblclick", llamar("dblclick"), true],
      ["keyup", llamar("sincronizar"), true],
      ["click", llamar("sincronizar"), true],
      ["select", llamar("sincronizar"), true],
      ["input", llamar("sincronizar"), true],
      ["focusin", llamar("sincronizar"), true],
      ["focusout", llamar("sincronizar"), true],
      ["scroll", llamar("reubicar"), true],
    ];
    for (const [ev, fn, cap] of pares) document.addEventListener(ev, fn, cap);
    const reubicar = llamar("reubicar");
    window.addEventListener("resize", reubicar);
    const vv = window.visualViewport;
    if (vv) vv.addEventListener("resize", reubicar);
    return () => {
      for (const [ev, fn, cap] of pares) document.removeEventListener(ev, fn, cap);
      window.removeEventListener("resize", reubicar);
      if (vv) vv.removeEventListener("resize", reubicar);
    };
  }, []);

  /* Después de cada dibujo: el cursor donde quedó pendiente (lo que se
     escribió por la planilla recién está en el input ahora), y releer qué se
     está editando (la celda pasa a `editando` con el primer «=»). */
  useLayoutEffect(() => {
    const p = caretPendiente.current;
    if (p) {
      const el = p.el === "tira" ? tiraRef.current : p.el;
      if (el && el.isConnected) {
        caretPendiente.current = null;
        if (document.activeElement !== el) el.focus();
        try { el.setSelectionRange(p.pos, p.pos); } catch { /* no es un input de texto */ }
      }
    }
    sincronizar();
  });

  /* Volver a poner el foco en la tira cuando se cambia de hoja con ella abierta
     (tocar la pestaña se lo llevó). */
  useEffect(() => {
    if (!tira || !tiraRef.current) return;
    if (document.activeElement !== tiraRef.current) {
      tiraRef.current.focus();
      try { tiraRef.current.setSelectionRange(tiraCaret.current, tiraCaret.current); } catch { /* idem */ }
    }
  }, [activa]); // eslint-disable-line react-hooks/exhaustive-deps

  // La tira se cae si su hoja ya no está (se borró, se deshizo el agregarla).
  useEffect(() => {
    if (tira && (tira.origen >= hojas.length || !activo)) setTira(null);
  }, [tira, hojas.length, activo]);

  // La opción marcada, a la vista dentro de la lista.
  useEffect(() => {
    const ul = listaRef.current;
    const li = ul && ul.querySelector('[aria-selected="true"]');
    if (li && typeof li.scrollIntoView === "function") li.scrollIntoView({ block: "nearest" });
  }, [marcada]);

  /* ── El «fx» ─────────────────────────────────────────────────────────── */

  // Adónde va la función elegida en el buscador: la fórmula que se estaba
  // escribiendo (en el cursor), o, si no había ninguna, la celda elegida.
  const destinoFx = () => {
    if (tira) return { el: tiraRef.current, tipo: "tira" };
    const u = ultimoFoco.current;
    if (u && u.el.isConnected && tipoDe(u.el) === u.tipo && esFormula(u.el.value)) return u;
    return null;
  };
  const insertarDesdeFx = (fn) => {
    setFx(false);
    const sinArgs = argumentosDeFirma(fn.firma).length === 0;
    const pedazo = sinArgs ? `${fn.nombre}()` : `${fn.nombre}(`;
    const d = destinoFx();
    if (d && d.el) {
      const valor = d.el.value;
      let desde = d.tipo === "tira" ? tiraCaret.current : (d.caret ?? valor.length);
      let hasta = d.tipo === "tira" ? desde : (d.fin ?? desde);
      // Sobre un nombre a medio escribir, lo reemplaza.
      const p = desde === hasta ? palabraEnCursor(valor, desde) : null;
      if (p && p.texto) { desde = p.desde; hasta = p.hasta; }
      const r = insertar(valor, desde, hasta, pedazo);
      aplicarTexto(d.el, d.tipo, r.texto, r.caret);
      return;
    }
    if (!sel || !barraRef || !barraRef.current) return;
    // Sin fórmula en curso: la celda elegida arranca una, desde la barra (así
    // queda a la vista entera y se sigue escribiendo ahí).
    const bar = barraRef.current;
    bar.focus();
    const texto = `=${pedazo}`;
    caretPendiente.current = { el: bar, pos: texto.length };
    escribir(texto);
  };

  if (!activo) return null;

  /* ── Lo que se ve ───────────────────────────────────────────────────── */

  const el = elRef.current;
  let lista = null;
  let ayuda = null;
  if (el && el.isConnected && (verLista || fnAyuda)) {
    const u = ubicar(el, 260);
    const listaAbajo = u.vaAbajo;
    if (verLista) {
      const alto = Math.max(120, Math.min(300, listaAbajo ? u.abajo : u.arriba));
      const estilo = listaAbajo
        ? { left: u.left, width: u.ancho, top: u.r.bottom + 4, maxHeight: alto }
        : { left: u.left, width: u.ancho, bottom: u.altoVentana - u.r.top + 4, maxHeight: alto };
      lista = (
        <ul
          ref={listaRef} className="asf-lista" role="listbox" aria-label="Funciones" style={estilo}
          // Tocar una opción no le saca el foco a lo que se edita.
          onMouseDown={(e) => e.preventDefault()}
        >
          {opciones.map((op, i) => (
            <li
              key={op.fn.nombre} role="option" aria-selected={i === marcada}
              className={i === marcada ? "marcada" : undefined}
              onClick={() => elegirFuncion(op)}
            >
              <Icono fn={op.fn} />
              <span className="asf-op">
                <span className="asf-op-nombre">
                  <b>{op.fn.nombre}</b>
                  {op.por !== op.fn.nombre && <span className="asf-alias"> ({op.por})</span>}
                  <span className="asf-op-args">{op.fn.firma.slice(op.fn.nombre.length)}</span>
                </span>
                <span className="asf-desc">{op.fn.descripcion}</span>
              </span>
            </li>
          ))}
        </ul>
      );
    }
    if (fnAyuda) {
      // La ayuda, del otro lado de la lista; sin lista, arriba si entra.
      const ayudaArriba = verLista ? listaAbajo : u.arriba >= 64 || u.arriba >= u.abajo;
      const estilo = ayudaArriba
        ? { left: u.left, maxWidth: u.ancho, bottom: u.altoVentana - u.r.top + 4 }
        : { left: u.left, maxWidth: u.ancho, top: u.r.bottom + 4 };
      ayuda = (
        <div className="asf-ayuda" role="tooltip" style={estilo}>
          <span className="asf-ayuda-linea"><Icono fn={fnAyuda} /><Firma fn={fnAyuda} indice={enFn.arg} /></span>
          <span className="asf-desc">{fnAyuda.descripcion}</span>
        </div>
      );
    }
  }

  return (
    <>
      <button
        type="button" className="asf-boton-fx" title="Buscar una función e insertarla" aria-label="Buscar función (fx)"
        // Que tocarlo no le saque el foco a la fórmula que se está escribiendo:
        // la función va donde estaba el cursor.
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => setFx(true)}
      ><i>f</i>x</button>
      {tira && (
        <div className="asf-tira" role="group" aria-label="Se está armando una fórmula de otra hoja">
          <span className="asf-tira-donde" title="La celda donde va la fórmula">
            {hojaEnFormula(hojas[tira.origen] ?? "")}!{refDe(tira.r, tira.c)}
          </span>
          <input
            ref={tiraRef} value={tira.valor} spellCheck={false} autoComplete="off"
            aria-label="Fórmula en curso"
            onChange={(e) => { const v = e.target.value; setTira((t) => (t ? { ...t, valor: v } : t)); }}
            onKeyDown={(e) => {
              if (e.key === "Enter") { e.preventDefault(); confirmarTira(); }
              else if (e.key === "Escape") { e.preventDefault(); cancelarTira(); }
            }}
          />
          <button type="button" className="esp-btn chico" onClick={confirmarTira} title="Listo (Enter)">✓ Listo</button>
          <button type="button" className="esp-btn chico" onClick={cancelarTira} aria-label="Volver sin cambiar la fórmula" title="Volver sin cambiarla (Esc)">×</button>
        </div>
      )}
      {lista}
      {ayuda}
      {fx && (
        <BuscadorFunciones
          puedeInsertar={!!destinoFx() || !!sel}
          onElegir={insertarDesdeFx}
          onCerrar={() => setFx(false)}
        />
      )}
    </>
  );
}
